import { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem, Alert, CircularProgress } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { convertOfferToOrder, type OfferListItem } from '../../api/oferte'
import { getClient } from '../../api/clienti'
import { listOrderWorkPoints } from '../../api/comenzi'
import { errorMessage } from '../../api/client'
import { useAuth } from '../../auth/AuthContext'

const schema = z.object({
  workPointId: z.string().min(1, 'Selectați punct de lucru'),
  deliveryAddressId: z.string().optional(),
  billingAddressId: z.string().optional(),
  clientPoReference: z.string().optional(),
  requestedDeliveryDate: z.string().min(1, 'Câmp obligatoriu'),
})
type FormValues = z.infer<typeof schema>

export default function ConvertToOrderDialog({ offer, onClose, onConverted }: {
  offer: OfferListItem | null; onClose: () => void; onConverted: (orderId: string) => void
}) {
  const open = offer !== null
  const qc = useQueryClient()
  const { user } = useAuth()
  const { data: client } = useQuery({
    queryKey: ['comercial', 'client', offer?.clientId ?? null], queryFn: () => getClient(offer!.clientId!),
    enabled: open && !!offer?.clientId,
  })
  const { data: workPoints } = useQuery({ queryKey: ['comercial', 'comenzi', 'puncte-lucru'], queryFn: listOrderWorkPoints, enabled: open })

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { workPointId: '', deliveryAddressId: '', billingAddressId: '', clientPoReference: '', requestedDeliveryDate: '' },
  })

  useEffect(() => {
    if (open) reset({ workPointId: user?.workPointId ?? '', deliveryAddressId: '', billingAddressId: '', clientPoReference: '', requestedDeliveryDate: '' })
  }, [open, reset, user])

  const mutation = useMutation({
    mutationFn: (values: FormValues) => convertOfferToOrder(offer!.id, {
      workPointId: values.workPointId,
      deliveryAddressId: values.deliveryAddressId || null,
      billingAddressId: values.billingAddressId || null,
      clientPoReference: values.clientPoReference || null,
      requestedDeliveryDate: values.requestedDeliveryDate,
    }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['comercial', 'oferte'] })
      void qc.invalidateQueries({ queryKey: ['comercial', 'comenzi'] })
      onConverted(r.id)
      onClose()
    },
  })

  const deliveryAddresses = client?.addresses.filter((a) => a.type === 'Livrare') ?? []
  const billingAddresses = client?.addresses.filter((a) => a.type === 'Facturare') ?? []

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>Conversie ofertă {offer?.offerNumber} în comandă</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Controller name="workPointId" control={control} render={({ field }) => (
              <TextField {...field} select label="Punct de lucru" fullWidth error={!!errors.workPointId} helperText={errors.workPointId?.message}>
                {workPoints?.map((w) => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}
              </TextField>
            )} />
            <Controller name="deliveryAddressId" control={control} render={({ field }) => (
              <TextField {...field} select label="Adresă de livrare (implicit dacă gol)" fullWidth>
                <MenuItem value="">Adresa implicită a clientului</MenuItem>
                {deliveryAddresses.map((a) => <MenuItem key={a.id} value={a.id!}>{a.line}, {a.city}</MenuItem>)}
              </TextField>
            )} />
            <Controller name="billingAddressId" control={control} render={({ field }) => (
              <TextField {...field} select label="Adresă de facturare (implicit dacă gol)" fullWidth>
                <MenuItem value="">Adresa implicită a clientului</MenuItem>
                {billingAddresses.map((a) => <MenuItem key={a.id} value={a.id!}>{a.line}, {a.city}</MenuItem>)}
              </TextField>
            )} />
            <Controller name="clientPoReference" control={control} render={({ field }) => (
              <TextField {...field} label="Referință comandă client" fullWidth />
            )} />
            <Controller name="requestedDeliveryDate" control={control} render={({ field }) => (
              <TextField {...field} type="date" label="Dată livrare solicitată" fullWidth slotProps={{ inputLabel: { shrink: true } }}
                error={!!errors.requestedDeliveryDate} helperText={errors.requestedDeliveryDate?.message} />
            )} />
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Convertește
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
